import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import { useAuth } from '../contexts/AuthContext';
import { submitKeysOf } from '../components/workflow/useWorkflowPerms';
import { IndependenceDeclaration } from '../components/workflow/IndependenceDeclaration';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import { EngagementBlockers, engagementBlockReasons } from '../components/workflow/RecordBlockers';
import ReviewerSelect from '../components/workflow/ReviewerSelect';
import { Field, FilePicker, InfoTip, inputCls, selectCls } from '../components/shared/FormKit';
import { AUDIT_OPINIONS } from './fsAuditOpinion';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import { useFsAutoDetect } from '../components/workflow/FsDateAutoDetect';
import { useFsAiReview } from '../components/workflow/FsAiReview';
import { fetchJSON } from '../utils/api';
import EngagementLeadsBox from '../components/workflow/EngagementLeadsBox';
import CompanyTabBar from '../components/workflow/CompanyTabBar';
import { engagementLabelsOf } from '../components/workflow/CoverageDisplay';
import { engagementDisplayName } from '../utils/engagementName';

// Signed Financial Statements — submission form (docs/modules/workflow-fs.md). fs-only
// (QC35 has its own flow at /workflow/qc35). One page files one or several
// companies of the same client group and FYE year — one tab per company, one
// request per company (POST /api/workflow/fs/batch).

// Client-side required check shared with the detail page's Modify modal → the
// blanks as `[{ key, label }]` (the server re-validates: POST and PATCH
// enforce the same signing-date and upload rules). `key` is the `field=` key of
// the control that is empty, so `formValidation` marks it on the page; `label`
// is the banner wording. `opts` carries what differs in the Modal context:
// - `engagementIds`: only the New form edits the coverage — omit to skip.
// - `existingFsFiles`: signed financial statements documents already on the request that the
//   save keeps; they satisfy the upload requirement, so a Modify need not
//   re-attach one.
// The independence declaration is deliberately NOT here: New asks it as its
// own gap, and a Modify never re-asks — it was recorded at submission and the
// server keeps it immutable.
export const fsRequiredGaps = (form, files, opts = {}) => {
  const { engagementIds = null, existingFsFiles = 0 } = opts;
  const gaps = [];
  if (engagementIds && engagementIds.length === 0) gaps.push({ key: 'engagements', label: 'Engagement' });
  if (!form.signingDate) gaps.push({ key: 'signingDate', label: 'Audit Report Signing Date' });
  if (!form.auditOpinion) gaps.push({ key: 'auditOpinion', label: 'Audit Opinion' });
  if ((files || []).length + existingFsFiles === 0) gaps.push({ key: 'filesFs', label: 'Upload Signed Financial Statements' });
  return gaps;
};

const yesNoSelect = (value, onChange) => (
  <div className="flex flex-wrap gap-4 pt-0.5">
    {[['yes', 'Yes'], ['no', 'No']].map(([v, label]) => (
      <label key={v} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="radio" checked={value === v} onChange={() => onChange(v)} />
        {label}
      </label>
    ))}
  </div>
);

// Shared field blocks (engreg pattern): the New form and the detail page's
// Modify modal render the very same controls, so label, hint and behaviour
// stay one source. Each `field=` key equals the detail page's CHANGE_LABELS
// key — that is what surfaces per-field return comments next to the input in
// the modal (FormKit's Field renders them; without a provider it is inert, so
// the New form is unaffected).
// `detect` (optional) mounts the stand-alone "Auto-detect from PDF" button —
// the Modify modal and the AI-disabled New form use it; the AI-enabled New
// form omits it (detection runs inside "Run AI review", and this field only
// appears on the manual lane, pre-filled with whatever was detected).
// `hint` lets the Management record form say what the date drives there
// (no reviewer on that surface).
export const FsSigningDateField = ({
  value, onChange, detect = null,
  hint = 'Verify against the signed audit report page — reviewers check it before approval.',
}) => (
  <Field
    label="Audit Report Signing Date"
    required
    field="signingDate"
    action={detect ? detect.button : undefined}
    hint={hint}
  >
    <input type="date" value={value} onChange={e => onChange(e.target.value)} className={inputCls} />
    {detect && detect.panel}
  </Field>
);

// The opinion type expressed in the signed audit report — mandatory on both
// lanes (AI and manual); the option list is shared with the server and the
// Management record form (fsAuditOpinion.js). A record created by the
// data-import tool carries no opinion and may stay blank (`required={false}`).
export const FsAuditOpinionField = ({ value, onChange, required = true }) => (
  <Field label="Audit Opinion" required={required} field="auditOpinion">
    <select value={value} onChange={e => onChange(e.target.value)} className={selectCls}>
      <option value="">{required ? 'Select…' : 'Not recorded (imported)'}</option>
      {AUDIT_OPINIONS.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);

export const TECH_TOOLS_TIP = "Automated tools and techniques (ATTs) are programs, applications and ready-to-use routines used by audit engagement teams to examine, sort, filter or analyse transactions or other data used as audit evidence or which generates results that supplement the audit engagement team's judgement. This excludes audit/assurance platforms.";

export const FsAuditQuestionFields = ({
  techToolsUsed, setTechToolsUsed, techToolsAreas, setTechToolsAreas,
  restatement, setRestatement, restatementDesc, setRestatementDesc,
}) => (
  <>
    <Field label={<>Were technological tools used in performing the audit? <InfoTip text={TECH_TOOLS_TIP} /></>} field="techToolsUsed">
      {yesNoSelect(techToolsUsed, setTechToolsUsed)}
    </Field>
    {techToolsUsed === 'yes' && (
      <Field label="Areas in Which the Tools Were Deployed" field="techToolsAreas">
        <textarea rows="2" value={techToolsAreas} onChange={e => setTechToolsAreas(e.target.value)} className={inputCls} />
      </Field>
    )}
    <Field label="Any restatement?" field="restatement">
      {yesNoSelect(restatement, setRestatement)}
    </Field>
    {restatement === 'yes' && (
      <Field label="Brief description of the facts giving rise to the restatement" field="restatementDesc">
        <textarea rows="2" value={restatementDesc} onChange={e => setRestatementDesc(e.target.value)} className={inputCls} />
      </Field>
    )}
  </>
);

export const FsRemarksField = ({ value, onChange }) => (
  <Field label="Remarks" field="remarks">
    <textarea rows="2" value={value} onChange={e => onChange(e.target.value)} className={inputCls} />
  </Field>
);

// The submitter names who approves, from the firm's Partners only — shared by
// the New form and the Modify modal (chosen-reviewer review,
// docs/workflow-contract.md §2a).
export const FsReviewerField = ({ value, onChange }) => (
  <Field label="Reviewer" required field="reviewerName"
    hint="You pick who reviews this submission, from the firm's Partners.">
    <ReviewerSelect value={value} onChange={onChange} grade="partner" />
  </Field>
);

// The questionnaire's starting answers — every new company tab here and every
// new Management record start from these.
export const FS_QUESTION_DEFAULTS = {
  techToolsUsed: 'yes',
  techToolsAreas: 'Caseware Analytics Hub - JE testing',
  restatement: 'no',
  restatementDesc: '',
};

const emptyCompany = () => ({
  files: [],
  signingDate: '',
  auditOpinion: '',
  ...FS_QUESTION_DEFAULTS,
});

// Duplicate guard, mirrored from the server: one open/approved Signed
// Financial Statements per engagement (legacy 'both' rows also cover fs).
// Returns the blocking reason, or null when it can be submitted.
const fsBlockedOf = (en) => {
  const c = en?.fsCoverage?.fs;
  if (!c) return null;
  if (c.approved) return `Signed Financial Statements already approved for ${engagementDisplayName(en.engagementName, en.serviceDescription)} (${c.approved.requestNo})`;
  if (c.pending) return `Signed Financial Statements already in review for ${engagementDisplayName(en.engagementName, en.serviceDescription)} (${c.pending.requestNo})`;
  return null;
};

// One company's tab. Every selected company keeps its panel mounted (hidden
// when it is not the active tab) so its upload, detection and AI verdict
// survive tab switches; the panel reports its AI state up through `onAi`.
const FsCompanyPanel = ({
  engagement, value, onChange, aiEnabled, saving, hidden,
  onAi, validationValue,
}) => {
  const id = engagement.id;
  const set = (key, v) => onChange({ [key]: v });

  // Fallback lane when the server has no AI configured: the stand-alone
  // auto-detect button on the always-visible date field (the original flow).
  const detect = useFsAutoDetect({
    files: value.files,
    onDetected: (d) => set('signingDate', d),
    disabled: saving || aiEnabled,
  });
  // The mandatory AI review gate (docs/modules/workflow-fs.md §7): a pass
  // approves this company's request automatically (no date field, no
  // reviewer); anything else reveals the date field and needs the reviewer.
  const ai = useFsAiReview({
    enabled: aiEnabled,
    engagementId: id,
    files: value.files,
    onDetected: (d) => set('signingDate', d),
    disabled: saving,
  });

  useEffect(() => {
    onAi(id, {
      busy: ai.busy || detect.busy,
      passed: ai.passed,
      aiReviewId: ai.aiReviewId,
      detectedDate: ai.detectedDate,
      needsReviewer: ai.needsReviewer,
    });
  }, [id, onAi, ai.busy, detect.busy, ai.passed, ai.aiReviewId, ai.detectedDate, ai.needsReviewer]);

  const blocked = fsBlockedOf(engagement);
  const existingSubmissions = engagement.fsCoverage?.fs
    ? ['approved', 'pending'].flatMap((state) => {
        const ref = engagement.fsCoverage.fs[state];
        return ref ? [{ ...ref, state }] : [];
      })
    : [];

  return (
    <div role="tabpanel" hidden={hidden} className={hidden ? 'hidden' : 'space-y-4'}>
      <FormValidationProvider value={validationValue}>
        <EngagementLeadsBox engagement={engagement} showSignOff />
        {existingSubmissions.length > 0 && (
          <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800">
            <p className="font-medium mb-1">
              {blocked
                ? `This engagement cannot receive another Signed Financial Statements submission — ${blocked}.`
                : 'Existing Signed Financial Statements submissions for this engagement:'}
            </p>
            {existingSubmissions.map(s => (
              <p key={`${s.id}-${s.state}`}>
                <RefLink to={`/workflow/fs/${s.id}`} className="font-medium underline hover:text-amber-900">{s.requestNo}</RefLink>
                {' — '}{s.state === 'approved' ? 'approved' : 'in review'}
              </p>
            ))}
          </div>
        )}

        <Field label="Upload Signed Financial Statements" required field="filesFs" hint={`PDF / DOC / DOCX, up to 30 MB per file. Upload a PDF to enable ${aiEnabled ? 'the AI review' : 'signing-date auto-detection'}.`}>
          <FilePicker
            multiple
            accept=".pdf,.doc,.docx"
            disabled={detect.busy || ai.busy}
            onChange={e => set('files', Array.from(e.target.files || []))}
            files={value.files}
          />
        </Field>

        {aiEnabled ? (
          <>
            {/* One click detects the signing date, then the AI checks the
                handwritten firm code on that page and the company against
                the BizFile; a pass approves this company's request
                automatically. */}
            <Field label="AI Review" required field="aiReview" hint="Mandatory before submitting.">
              {ai.panel}
            </Field>
            {/* Manual lane only: the requester types the date themselves
                (pre-filled when detection saw one). */}
            {ai.needsReviewer && (
              <FsSigningDateField value={value.signingDate} onChange={v => set('signingDate', v)} />
            )}
          </>
        ) : (
          <FsSigningDateField value={value.signingDate} onChange={v => set('signingDate', v)} detect={detect} />
        )}

        <FsAuditOpinionField value={value.auditOpinion} onChange={v => set('auditOpinion', v)} />

        <FsAuditQuestionFields
          techToolsUsed={value.techToolsUsed} setTechToolsUsed={v => set('techToolsUsed', v)}
          techToolsAreas={value.techToolsAreas} setTechToolsAreas={v => set('techToolsAreas', v)}
          restatement={value.restatement} setRestatement={v => set('restatement', v)}
          restatementDesc={value.restatementDesc} setRestatementDesc={v => set('restatementDesc', v)}
        />
      </FormValidationProvider>
    </div>
  );
};

const WorkflowFsNew = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [engagements, setEngagements] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Coverage: several companies of one project (client group + FYE year);
  // each gets its own tab and becomes its own request.
  const [engagementIds, setEngagementIds] = useState([]);
  const [companies, setCompanies] = useState({}); // { [id]: emptyCompany() shape }
  const [aiStates, setAiStates] = useState({});   // { [id]: { busy, passed, aiReviewId, detectedDate, needsReviewer } }
  const [activeId, setActiveId] = useState(null);
  const [independence, setIndependence] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [reviewerStaffId, setReviewerStaffId] = useState('');

  const canSubmit = submitKeysOf('fs').some(hasPermission);
  const engOf = (id) => engagements.find(e => e.id === id) || null;
  const selectedAll = engagements.filter(e => engagementIds.includes(e.id));
  const multi = engagementIds.length > 1;
  // Company name, plus service / description when two tabs share the company.
  const labels = engagementLabelsOf(engagementIds.map(engOf));
  const nameOf = (id) => labels[id]?.text || `#${id}`;

  useEffect(() => {
    if (!canSubmit) return;
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/fs/options');
        setEngagements(data.engagements || []);
        setAiEnabled(data.aiReview?.enabled === true);
      } catch (e) { setError(e.message); }
    })();
  }, [canSubmit]);

  // Selection change: a new company opens a fresh tab, a removed one drops
  // its tab (and whatever was uploaded there).
  const updateSelection = (ids) => {
    setError(null);
    setEngagementIds(ids);
    setCompanies(prev => Object.fromEntries(ids.map(id => [id, prev[id] || emptyCompany()])));
    setAiStates(prev => Object.fromEntries(ids.filter(id => prev[id]).map(id => [id, prev[id]])));
    setActiveId(cur => (ids.includes(cur) ? cur : (ids[0] || null)));
  };
  const patchCompany = (id, part) => setCompanies(prev => ({ ...prev, [id]: { ...prev[id], ...part } }));
  const onAi = useCallback((id, state) => setAiStates(prev => ({ ...prev, [id]: state })), []);

  // Manual lane per company = AI off, or its AI verdict says a human must review.
  const aiOf = (id) => aiStates[id] || {};
  const manualLane = (id) => !aiEnabled || aiOf(id).needsReviewer === true;
  const anyManual = engagementIds.some(manualLane);
  const anyBusy = engagementIds.some(id => aiOf(id).busy);

  // Each company's own blanks (key = the field= key inside its tab).
  const companyGaps = (id) => {
    const c = companies[id] || emptyCompany();
    const a = aiOf(id);
    return [
      ...(c.files.length === 0 ? [{ key: 'filesFs', label: 'Upload Signed Financial Statements' }] : []),
      ...(aiEnabled && !a.needsReviewer && !a.passed ? [{ key: 'aiReview', label: 'AI Review' }] : []),
      ...(manualLane(id) && !c.signingDate ? [{ key: 'signingDate', label: 'Audit Report Signing Date' }] : []),
      ...(c.auditOpinion ? [] : [{ key: 'auditOpinion', label: 'Audit Opinion' }]),
    ];
  };

  // Everything still empty — after the first Submit the page marks those
  // controls (each tab marks its own) and lists them in the banner, named by
  // company when there are several.
  const gaps = useMemo(() => [
    ...(engagementIds.length === 0 ? [{ key: 'engagements', label: 'Engagement' }] : []),
    ...engagementIds.flatMap(id => companyGaps(id).map(g => ({
      key: `${id}:${g.key}`,
      label: multi ? `${nameOf(id)} — ${g.label}` : g.label,
    }))),
    ...(anyManual && !reviewerStaffId ? [{ key: 'reviewerName', label: 'Reviewer' }] : []),
    ...(independence ? [] : [{ key: 'independenceDeclared', label: 'Independence Declaration' }]),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [engagementIds, companies, aiStates, aiEnabled, reviewerStaffId, independence, anyManual, multi, engagements]);
  const validation = useFormValidation(gaps);

  // Per-tab provider: the marks of that company only, with the tab prefix
  // stripped so the shared field blocks keep their plain field= keys.
  const panelValidation = (id) => {
    const byKey = {};
    const prefix = `${id}:`;
    for (const [k, g] of Object.entries(validation.providerValue.byKey)) {
      if (k.startsWith(prefix)) byKey[k.slice(prefix.length)] = g;
    }
    return { byKey };
  };

  const blockedReasons = selectedAll.map(fsBlockedOf).filter(Boolean);
  const crossBlocked = engagementBlockReasons(selectedAll, 'fs').length > 0;

  const tabTag = (id) => {
    const a = aiOf(id);
    const missing = companyGaps(id).length;
    if (fsBlockedOf(engOf(id))) return { text: 'Blocked', tone: 'todo' };
    if (a.busy) return { text: 'Reviewing', tone: 'info' };
    if (missing > 0) {
      return validation.attempted
        ? { text: `${missing} Missing`, tone: 'todo' }
        : { text: 'Incomplete', tone: 'muted' };
    }
    if (aiEnabled && a.passed) return { text: 'AI Passed', tone: 'done', title: 'Approved automatically on submit' };
    return { text: 'Manual Review', tone: 'info', title: 'Goes to the reviewer below' };
  };

  const submit = async () => {
    // Marks the empty fields; a tab with gaps comes to the front so the
    // first mark is visible.
    if (!validation.validate()) {
      setError(null);
      const first = engagementIds.find(id => companyGaps(id).length > 0);
      if (first) setActiveId(first);
      return;
    }
    if (blockedReasons.length > 0) {
      setError(`These engagements cannot receive a Signed Financial Statements submission: ${blockedReasons.join('; ')}.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      const triBool = (v) => (v === '' ? '' : String(v === 'yes'));
      form.set('items', JSON.stringify(engagementIds.map(id => {
        const c = companies[id];
        const a = aiOf(id);
        const passed = aiEnabled && a.passed;
        return {
          engagementId: id,
          // A passing AI review files the DETECTED date (there is no date
          // field on that lane); the manual lane files what was typed.
          signingDate: passed ? a.detectedDate : c.signingDate,
          auditOpinion: c.auditOpinion,
          techToolsUsed: triBool(c.techToolsUsed),
          techToolsAreas: c.techToolsUsed === 'yes' ? c.techToolsAreas : '',
          restatement: triBool(c.restatement),
          restatementDesc: c.restatementDesc,
          // AI pass ⇒ the voucher rides instead of the reviewer.
          aiReviewId: passed ? a.aiReviewId : null,
        };
      })));
      form.set('independenceDeclared', 'true');
      form.set('remarks', remarks);
      if (anyManual) form.set('reviewerStaffId', reviewerStaffId);
      engagementIds.forEach(id => companies[id].files.forEach(f => form.append(`filesFs_${id}`, f)));

      const data = await fetchJSON('/api/workflow/fs/batch', { method: 'POST', body: form });
      const created = (data.results || []).filter(r => r.request);
      const failed = (data.results || []).filter(r => r.error);
      if (failed.length === 0) {
        navigate(`/workflow/fs/${created[0].request.id}`);
        return;
      }
      // Part of it went through: drop the filed companies, keep the failed
      // tabs on the page with the reason so they can be sent again.
      updateSelection(failed.map(r => r.engagementId));
      setError(`${created.length > 0 ? `${created.map(r => r.request.requestNo).join(', ')} submitted. ` : ''}`
        + `Not submitted: ${failed.map(r => `${nameOf(r.engagementId)} (${r.error})`).join('; ')}.`);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/fs"
      backLabel="Signed Financial Statements"
      title="New Signed Financial Statements Request"
      subtitle="Upload the signed financial statements."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={blockedReasons.length > 0 || crossBlocked || anyBusy}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement(s)">
          <Field label="Engagement(s)" required field="engagements" hint={<>Only engagements you are a member of whose QC6/QC7 has been approved are listed. <InfoTip text="Each company becomes its own request with its own reviewer decision. Companies submitted together must belong to the same client group with an FYE in the same year." /></>}>
            <GroupedEngagementPicker
              engagements={engagements}
              selectedIds={engagementIds}
              onChange={updateSelection}
            />
            <EngagementBlockers selected={selectedAll} flow="fs" />
          </Field>
        </FormSection>

        <FormSection
          step={2}
          title="Submission"
          hint={multi ? 'One tab per company — each tab is filed as its own request.' : null}
        >
          {engagementIds.length === 0 ? (
            <p className="text-sm text-slate-500">Select the engagement(s) above first.</p>
          ) : (
            <>
              {multi && (
                <CompanyTabBar
                  items={engagementIds.map(id => ({
                    id,
                    label: labels[id]?.label || `#${id}`,
                    sub: labels[id]?.sub,
                    title: engagementDisplayName(engOf(id)?.engagementName, engOf(id)?.serviceDescription),
                    tag: tabTag(id),
                  }))}
                  activeId={activeId}
                  onSelect={setActiveId}
                />
              )}
              {engagementIds.map(id => {
                const en = engOf(id);
                if (!en || !companies[id]) return null;
                return (
                  <FsCompanyPanel
                    key={id}
                    engagement={en}
                    value={companies[id]}
                    onChange={part => patchCompany(id, part)}
                    aiEnabled={aiEnabled}
                    saving={saving}
                    hidden={id !== activeId}
                    onAi={onAi}
                    validationValue={panelValidation(id)}
                  />
                );
              })}
            </>
          )}
        </FormSection>

        <FormSection step={3} title="Declaration & Remarks">
          <Field label="Independence Declaration (applies to all persons on the engagement)" required field="independenceDeclared">
            <IndependenceDeclaration
              checked={independence}
              onChange={setIndependence}
              confirmLabel={multi
                ? 'I confirm all of the above on behalf of every person named on these engagements.'
                : 'I confirm all of the above on behalf of every person named on this engagement.'}
            />
          </Field>

          <FsRemarksField value={remarks} onChange={setRemarks} />
        </FormSection>

        {/* Only when a company is on the manual lane — AI-passed companies need
            no reviewer. One Partner reviews every manual request of the page. */}
        {anyManual && (
          <FormSection
            step={4}
            title="Reviewer"
            hint={multi && aiEnabled
              ? `Reviews the companies whose AI review did not pass (${engagementIds.filter(manualLane).map(nameOf).join(', ')}).`
              : null}
          >
            <FsReviewerField value={reviewerStaffId} onChange={setReviewerStaffId} />
          </FormSection>
        )}
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowFsNew;
